import { Controller } from 'react-hook-form';
import { Field, FieldRow } from '../../../forms/fields';
import type { SectionProps } from '../sectionProps';
import { type AnyFieldError, toFieldError } from './fieldError';

/**
 * Shared GROWTH/INDEXING field (PLAN.md M7 — "stream growth fixed|cpi"). Income and expense streams
 * carry EITHER the legacy scalar `growth` OR the enrichment `growthMode` discriminated union:
 *  - "Fixed rate"   → `growthMode = {kind:'fixed', rate}` (constant nominal growth);
 *  - "CPI-indexed"  → `growthMode = {kind:'cpi', realRate}` (track realized inflation + a real drift).
 *
 * We always write `growthMode` (the explicit, unambiguous form the engine prefers), leaving the
 * legacy scalar at its default — so a stream the editor touches has one source of truth and the
 * `.strict()` schema never sees a conflicting pair. The numeric rate is entered as a PERCENT in the
 * UI (3 → 0.03) for readability, and stored as the engine's decimal.
 */

export interface GrowthFieldProps {
  form: SectionProps['form'];
  /** Path to the stream's `growthMode`, e.g. `incomes.0.growthMode`. */
  name: `incomes.${number}.growthMode` | `expenses.${number}.growthMode`;
  error?: AnyFieldError;
}

type GrowthModeValue = { kind: 'fixed'; rate: number } | { kind: 'cpi'; realRate: number };

function toPercent(decimal: number): number {
  // Round-trip-safe: 0.03 -> 3, avoiding float dust like 2.9999999996.
  return Math.round(decimal * 1000) / 10;
}

export function GrowthField({ form, name, error }: GrowthFieldProps): React.ReactElement {
  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => {
        // When `growthMode` is unset (legacy plan), default the UI to a 0% fixed mode.
        const raw = (field.value as GrowthModeValue | undefined) ?? { kind: 'fixed', rate: 0 };
        const kind = raw.kind;
        const ratePercent = kind === 'cpi' ? toPercent(raw.realRate) : toPercent(raw.rate);

        return (
          <FieldRow>
            <div style={{ flex: 1, minWidth: 150 }}>
              <Field label="Growth mode" htmlFor={`${name}.kind`}>
                <select
                  id={`${name}.kind`}
                  className="select"
                  value={kind}
                  onBlur={field.onBlur}
                  onChange={(e) => {
                    const nextKind = e.currentTarget.value as 'fixed' | 'cpi';
                    field.onChange(
                      nextKind === 'cpi'
                        ? { kind: 'cpi', realRate: 0 }
                        : { kind: 'fixed', rate: 0 },
                    );
                  }}
                >
                  <option value="fixed">Fixed rate</option>
                  <option value="cpi">CPI-indexed</option>
                </select>
              </Field>
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <Field
                label={kind === 'cpi' ? 'Real drift % / yr' : 'Growth % / yr'}
                error={toFieldError(error)}
                htmlFor={`${name}.rate`}
                hint={kind === 'cpi' ? 'On top of realized inflation' : undefined}
              >
                <input
                  id={`${name}.rate`}
                  className="input input--num"
                  type="number"
                  step="0.1"
                  value={Number.isNaN(ratePercent) ? '' : ratePercent}
                  onBlur={field.onBlur}
                  onChange={(e) => {
                    const decimal = e.currentTarget.valueAsNumber / 100;
                    field.onChange(
                      kind === 'cpi'
                        ? { kind: 'cpi', realRate: decimal }
                        : { kind: 'fixed', rate: decimal },
                    );
                  }}
                />
              </Field>
            </div>
          </FieldRow>
        );
      }}
    />
  );
}
